import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTRPC } from '@/trpc/react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/super_admin/notifications-debug')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const pingNotificationsMutation = useMutation({
    ...api.superAdmin.pingNotifications.mutationOptions({}),
  })

  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)

  const listClientsWithNotificationPrefsMutation = useMutation({
    ...api.superAdmin.listClientsWithNotificationPrefs.mutationOptions({
      onSuccess: (clients) => {
        if (clients.length > 0 && !selectedClientId) {
          setSelectedClientId(clients[0]?.clientId ?? null)
        }
      },
    }),
  })

  const getClientNotificationPrefsMutation = useMutation({
    ...api.superAdmin.getClientNotificationPrefs.mutationOptions({
      onSuccess: (prefs) => {
        if (prefs) {
          setFormClientId(selectedClientId)
          setFormBusinessId(
            listClientsWithNotificationPrefsMutation.data?.find(
              (c) => c.clientId === selectedClientId,
            )?.businessId ?? null,
          )
          setFormEnabled(prefs.enabled)
          // Use first slot's notificationTime if slots exist, otherwise default
          setFormNotificationTime(
            prefs.notificationSlots && prefs.notificationSlots.length > 0
              ? prefs.notificationSlots[0]!.notificationTime
              : '09:00',
          )
          setFormAdvanceMinutes(prefs.advanceMinutes)
          setFormMethods(prefs.methods)
          setFormRecipientEmails(prefs.recipientEmails)
          setFormRecipientPhoneNumbers(prefs.recipientPhoneNumbers)
        }
      },
    }),
  })

  const [allClients, setAllClients] = useState<
    Array<{ clientId: string; businessId: string; clientName: string }>
  >([])
  const getAllClientsMutation = useMutation({
    ...api.superAdmin.getAllClients.mutationOptions({
      onSuccess: (clients) => {
        setAllClients(clients)
      },
    }),
  })

  const [formClientId, setFormClientId] = useState<string | null>(null)
  const [formBusinessId, setFormBusinessId] = useState<string | null>(null)
  const [formEnabled, setFormEnabled] = useState(false)
  const [formNotificationTime, setFormNotificationTime] = useState('09:00')
  const [formAdvanceMinutes, setFormAdvanceMinutes] = useState(15)
  const [formMethods, setFormMethods] = useState<Array<'email' | 'sms' | 'both'>>(['email'])
  const [formRecipientEmails, setFormRecipientEmails] = useState<string[]>([''])
  const [formRecipientPhoneNumbers, setFormRecipientPhoneNumbers] = useState<string[]>([''])

  const [clockInCheckClientId, setClockInCheckClientId] = useState<string | null>(null)
  const [clockInCheckDate, setClockInCheckDate] = useState<string>(
    new Date().toISOString().split('T')[0] ?? '',
  )

  const checkClientClockInsMutation = useMutation({
    ...api.superAdmin.checkClientClockIns.mutationOptions({}),
  })

  const [enqueueJobClientId, setEnqueueJobClientId] = useState<string | null>(null)
  const [enqueueJobTargetDate, setEnqueueJobTargetDate] = useState<string>(
    new Date().toISOString().split('T')[0] ?? '',
  )
  const [enqueueJobNotificationTime, setEnqueueJobNotificationTime] = useState<string>('09:00')
  const [enqueueJobTimeRangeStart, setEnqueueJobTimeRangeStart] = useState<string>('06:00')
  const [enqueueJobTimeRangeEnd, setEnqueueJobTimeRangeEnd] = useState<string>('09:00')
  const [enqueueJobSlotIndex, setEnqueueJobSlotIndex] = useState<number>(0)
  const [enqueueJobAdvanceMinutes, setEnqueueJobAdvanceMinutes] = useState<number>(15)
  const [enqueueJobDelayMs, setEnqueueJobDelayMs] = useState<number>(0)

  const enqueueNotificationTestJobMutation = useMutation({
    ...api.superAdmin.enqueueNotificationTestJob.mutationOptions({
      onSuccess: () => {
        toast.success('Job enqueued successfully')
      },
      onError: (error) => {
        toast.error(`Failed to enqueue job: ${error.message}`)
      },
    }),
  })

  const [testEmailRecipients, setTestEmailRecipients] = useState<string[]>([''])
  const [testEmailSubjectOverride, setTestEmailSubjectOverride] = useState<string>('')
  const [testEmailClientName, setTestEmailClientName] = useState<string>('Test Client')
  const [testEmailTargetDate, setTestEmailTargetDate] = useState<string>(
    new Date().toISOString().split('T')[0] ?? '',
  )
  const [testEmailNotificationTime, setTestEmailNotificationTime] = useState<string>('09:00')
  const [testEmailTimeRangeStart, setTestEmailTimeRangeStart] = useState<string>('06:00')
  const [testEmailTimeRangeEnd, setTestEmailTimeRangeEnd] = useState<string>('09:00')
  const [testEmailAdvanceMinutes, setTestEmailAdvanceMinutes] = useState<number>(15)

  const sendTestEmailMutation = useMutation({
    ...api.superAdmin.sendTestMissingClockInEmail.mutationOptions({
      onSuccess: () => {
        toast.success('Test email sent successfully')
      },
      onError: (error) => {
        toast.error(`Failed to send test email: ${error.message}`)
      },
    }),
  })

  const [testSMSRecipients, setTestSMSRecipients] = useState<string[]>([''])
  const [testSMSClientName, setTestSMSClientName] = useState<string>('Test Client')
  const [testSMSTargetDate, setTestSMSTargetDate] = useState<string>(
    new Date().toISOString().split('T')[0] ?? '',
  )
  const [testSMSNotificationTime, setTestSMSNotificationTime] = useState<string>('09:00')
  const [testSMSTimeRangeStart, setTestSMSTimeRangeStart] = useState<string>('06:00')
  const [testSMSTimeRangeEnd, setTestSMSTimeRangeEnd] = useState<string>('09:00')
  const [testSMSAdvanceMinutes, setTestSMSAdvanceMinutes] = useState<number>(15)

  const sendTestSMSMutation = useMutation({
    ...api.superAdmin.sendTestMissingClockInSMS.mutationOptions({
      onSuccess: () => {
        toast.success('Test SMS placeholder executed successfully')
      },
      onError: (error) => {
        toast.error(`Failed to send test SMS: ${error.message}`)
      },
    }),
  })

  const [schedulerTargetDate, setSchedulerTargetDate] = useState<string>(
    new Date().toISOString().split('T')[0] ?? '',
  )

  const dryRunScheduleMutation = useMutation({
    ...api.superAdmin.dryRunScheduleDailyNotifications.mutationOptions({
      onSuccess: () => {
        toast.success('Dry run completed')
      },
      onError: (error) => {
        toast.error(`Dry run failed: ${error.message}`)
      },
    }),
  })

  const runScheduleNowMutation = useMutation({
    ...api.superAdmin.runScheduleDailyNotificationsNow.mutationOptions({
      onSuccess: (result) => {
        toast.success(`Scheduled ${result.jobsEnqueued} jobs for ${result.totalClients} clients`)
      },
      onError: (error) => {
        toast.error(`Failed to schedule jobs: ${error.message}`)
      },
    }),
  })

  const createOrUpdateNotificationPrefsMutation = useMutation({
    ...api.superAdmin.createOrUpdateClientNotificationPrefs.mutationOptions({
      onSuccess: () => {
        toast.success('Notification preferences saved')
        listClientsWithNotificationPrefsMutation.mutate()
      },
      onError: (error) => {
        toast.error(`Failed to save: ${error.message}`)
      },
    }),
  })

  return (
    <DashboardShell>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold tracking-tight">Notifications Debug</h1>
            <p className="text-muted-foreground">
              Super admin-only tools for testing scheduled missing clock-in notifications.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Infra</CardTitle>
              <CardDescription>Server + Redis connectivity for notifications</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button
                className="w-full"
                disabled={pingNotificationsMutation.isPending}
                onClick={() => pingNotificationsMutation.mutate()}
              >
                Ping Notifications Infra (Step 3)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    status: pingNotificationsMutation.status,
                    data: pingNotificationsMutation.data ?? null,
                    error: pingNotificationsMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notification preferences</CardTitle>
              <CardDescription>Read client notification preferences</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button
                className="w-full"
                disabled={listClientsWithNotificationPrefsMutation.isPending}
                onClick={() => listClientsWithNotificationPrefsMutation.mutate()}
              >
                List clients with enabled preferences (Step 4)
              </Button>

              <Select
                value={selectedClientId ?? undefined}
                onValueChange={(value) => setSelectedClientId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {(listClientsWithNotificationPrefsMutation.data ?? []).map((client) => (
                    <SelectItem key={client.clientId} value={client.clientId}>
                      {client.clientName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                className="w-full"
                disabled={getClientNotificationPrefsMutation.isPending || !selectedClientId}
                onClick={() => {
                  if (!selectedClientId) {
                    return
                  }
                  getClientNotificationPrefsMutation.mutate({ clientId: selectedClientId })
                }}
              >
                Get preferences for selected client (Step 4)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    list: {
                      status: listClientsWithNotificationPrefsMutation.status,
                      data: listClientsWithNotificationPrefsMutation.data ?? null,
                      error: listClientsWithNotificationPrefsMutation.error?.message ?? null,
                    },
                    get: {
                      status: getClientNotificationPrefsMutation.status,
                      data: getClientNotificationPrefsMutation.data ?? null,
                      error: getClientNotificationPrefsMutation.error?.message ?? null,
                    },
                  },
                  null,
                  2,
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Create/Update notification preferences</CardTitle>
              <CardDescription>
                Add or update notification preferences for a client
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Button
                className="w-full"
                disabled={getAllClientsMutation.isPending}
                onClick={() => getAllClientsMutation.mutate()}
              >
                Load all clients
              </Button>

              <div className="flex flex-col gap-2">
                <Label>Client</Label>
                <Select
                  value={formClientId ?? undefined}
                  onValueChange={(value) => {
                    setFormClientId(value)
                    const client = allClients.find((c) => c.clientId === value)
                    if (client) {
                      setFormBusinessId(client.businessId)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {allClients.map((client) => (
                      <SelectItem key={client.clientId} value={client.clientId}>
                        {client.clientName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="enabled"
                  checked={formEnabled}
                  onCheckedChange={(checked) => setFormEnabled(checked === true)}
                />
                <Label htmlFor="enabled">Enabled</Label>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Notification time (HH:mm)</Label>
                <Input
                  type="time"
                  value={formNotificationTime}
                  onChange={(e) => setFormNotificationTime(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Advance minutes</Label>
                <Input
                  type="number"
                  value={formAdvanceMinutes}
                  onChange={(e) => setFormAdvanceMinutes(Number.parseInt(e.target.value, 10))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Methods</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-email"
                      checked={formMethods.includes('email')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormMethods([...formMethods.filter((m) => m !== 'email'), 'email'])
                        } else {
                          setFormMethods(formMethods.filter((m) => m !== 'email'))
                        }
                      }}
                    />
                    <Label htmlFor="method-email">Email</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-sms"
                      checked={formMethods.includes('sms')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormMethods([...formMethods.filter((m) => m !== 'sms'), 'sms'])
                        } else {
                          setFormMethods(formMethods.filter((m) => m !== 'sms'))
                        }
                      }}
                    />
                    <Label htmlFor="method-sms">SMS</Label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient emails (one per line)</Label>
                <textarea
                  className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formRecipientEmails.join('\n')}
                  onChange={(e) =>
                    setFormRecipientEmails(
                      e.target.value.split('\n').filter((email) => email.trim() !== ''),
                    )
                  }
                  placeholder="email1@example.com&#10;email2@example.com"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient phone numbers (one per line, optional)</Label>
                <textarea
                  className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formRecipientPhoneNumbers.join('\n')}
                  onChange={(e) =>
                    setFormRecipientPhoneNumbers(
                      e.target.value.split('\n').filter((phone) => phone.trim() !== ''),
                    )
                  }
                  placeholder="+1234567890&#10;+0987654321"
                />
              </div>

              <Button
                className="w-full"
                disabled={
                  createOrUpdateNotificationPrefsMutation.isPending ||
                  !formClientId ||
                  !formBusinessId ||
                  formMethods.length === 0 ||
                  formRecipientEmails.length === 0
                }
                onClick={() => {
                  if (!formClientId || !formBusinessId) {
                    return
                  }
                  createOrUpdateNotificationPrefsMutation.mutate({
                    clientId: formClientId,
                    businessId: formBusinessId,
                    preferences: {
                      enabled: formEnabled,
                      notificationSlots: [
                        {
                          notificationTime: formNotificationTime,
                          timeRangeStart: '06:00', // Default time range start
                          timeRangeEnd: formNotificationTime, // Default time range end matches notification time
                        },
                      ],
                      advanceMinutes: formAdvanceMinutes,
                      methods: formMethods,
                      recipientEmails: formRecipientEmails.filter((e) => e.trim() !== ''),
                      recipientPhoneNumbers: formRecipientPhoneNumbers.filter(
                        (p) => p.trim() !== '',
                      ),
                    },
                  })
                }}
              >
                Save notification preferences
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    status: createOrUpdateNotificationPrefsMutation.status,
                    data: createOrUpdateNotificationPrefsMutation.data ?? null,
                    error: createOrUpdateNotificationPrefsMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Clock-in detection</CardTitle>
              <CardDescription>
                Check if a client has clock-ins for a specific date. This function fetches the
                client's notification preferences first, then checks for timesheets. This is the
                same logic used by the notification processor.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <Label>Client</Label>
                <Select
                  value={clockInCheckClientId ?? undefined}
                  onValueChange={(value) => setClockInCheckClientId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {(listClientsWithNotificationPrefsMutation.data ?? []).map((client) => (
                      <SelectItem key={client.clientId} value={client.clientId}>
                        {client.clientName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Date (yyyy-MM-dd)</Label>
                <Input
                  type="date"
                  value={clockInCheckDate}
                  onChange={(e) => setClockInCheckDate(e.target.value)}
                />
              </div>

              <Button
                className="w-full"
                disabled={
                  checkClientClockInsMutation.isPending ||
                  !clockInCheckClientId ||
                  !clockInCheckDate
                }
                onClick={() => {
                  if (!clockInCheckClientId || !clockInCheckDate) {
                    return
                  }
                  checkClientClockInsMutation.mutate({
                    clientId: clockInCheckClientId,
                    dateIso: clockInCheckDate,
                  })
                }}
              >
                Check client clock-ins (Step 5)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    status: checkClientClockInsMutation.status,
                    data: checkClientClockInsMutation.data ?? null,
                    error: checkClientClockInsMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Queue</CardTitle>
              <CardDescription>
                Enqueue test notification jobs. Jobs will remain pending until a worker is added
                (Step 7).
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>Client</Label>
                <Select
                  value={enqueueJobClientId ?? undefined}
                  onValueChange={(value) => setEnqueueJobClientId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {(listClientsWithNotificationPrefsMutation.data ?? []).map((client) => (
                      <SelectItem key={client.clientId} value={client.clientId}>
                        {client.clientName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Target date (yyyy-MM-dd)</Label>
                <Input
                  type="date"
                  value={enqueueJobTargetDate}
                  onChange={(e) => setEnqueueJobTargetDate(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Notification time (HH:mm)</Label>
                <Input
                  type="time"
                  value={enqueueJobNotificationTime}
                  onChange={(e) => setEnqueueJobNotificationTime(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Time range start (HH:mm)</Label>
                <Input
                  type="time"
                  value={enqueueJobTimeRangeStart}
                  onChange={(e) => setEnqueueJobTimeRangeStart(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Time range end (HH:mm)</Label>
                <Input
                  type="time"
                  value={enqueueJobTimeRangeEnd}
                  onChange={(e) => setEnqueueJobTimeRangeEnd(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Slot index</Label>
                <Input
                  type="number"
                  value={enqueueJobSlotIndex}
                  onChange={(e) =>
                    setEnqueueJobSlotIndex(Number.parseInt(e.target.value, 10) || 0)
                  }
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Advance minutes</Label>
                <Input
                  type="number"
                  value={enqueueJobAdvanceMinutes}
                  onChange={(e) =>
                    setEnqueueJobAdvanceMinutes(Number.parseInt(e.target.value, 10) || 0)
                  }
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Delay (ms) - optional</Label>
                <Input
                  type="number"
                  value={enqueueJobDelayMs}
                  onChange={(e) => setEnqueueJobDelayMs(Number.parseInt(e.target.value, 10) || 0)}
                  placeholder="0"
                />
              </div>

              <Button
                className="w-full"
                disabled={
                  enqueueNotificationTestJobMutation.isPending ||
                  !enqueueJobClientId ||
                  !enqueueJobTargetDate ||
                  !enqueueJobNotificationTime ||
                  !enqueueJobTimeRangeStart ||
                  !enqueueJobTimeRangeEnd
                }
                onClick={() => {
                  if (
                    !enqueueJobClientId ||
                    !enqueueJobTargetDate ||
                    !enqueueJobNotificationTime ||
                    !enqueueJobTimeRangeStart ||
                    !enqueueJobTimeRangeEnd
                  ) {
                    return
                  }
                  enqueueNotificationTestJobMutation.mutate({
                    clientId: enqueueJobClientId,
                    targetDate: enqueueJobTargetDate,
                    notificationTime: enqueueJobNotificationTime,
                    timeRangeStart: enqueueJobTimeRangeStart,
                    timeRangeEnd: enqueueJobTimeRangeEnd,
                    slotIndex: enqueueJobSlotIndex,
                    advanceMinutes: enqueueJobAdvanceMinutes,
                    delayMs: enqueueJobDelayMs > 0 ? enqueueJobDelayMs : undefined,
                  })
                }}
              >
                Enqueue test job (Step 6)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    status: enqueueNotificationTestJobMutation.status,
                    data: enqueueNotificationTestJobMutation.data ?? null,
                    error: enqueueNotificationTestJobMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>

              <Button disabled className="w-full">
                Enqueue + process (worker) (Step 7+)
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Scheduler</CardTitle>
              <CardDescription>
                Dry-run and schedule daily notification jobs for all enabled clients
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>Target date (optional, defaults to today)</Label>
                <Input
                  type="date"
                  value={schedulerTargetDate}
                  onChange={(e) => setSchedulerTargetDate(e.target.value)}
                />
              </div>

              <Button
                className="w-full"
                disabled={dryRunScheduleMutation.isPending}
                onClick={() => {
                  dryRunScheduleMutation.mutate({
                    targetDate: schedulerTargetDate || undefined,
                  })
                }}
              >
                Dry run daily schedule (Step 11)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-auto">
                {JSON.stringify(
                  {
                    status: dryRunScheduleMutation.status,
                    data: dryRunScheduleMutation.data ?? null,
                    error: dryRunScheduleMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>

              <Button
                className="w-full"
                disabled={runScheduleNowMutation.isPending}
                onClick={() => {
                  runScheduleNowMutation.mutate({
                    targetDate: schedulerTargetDate || undefined,
                  })
                }}
              >
                Schedule daily notifications now (Step 11)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-auto">
                {JSON.stringify(
                  {
                    status: runScheduleNowMutation.status,
                    data: runScheduleNowMutation.data ?? null,
                    error: runScheduleNowMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Delivery</CardTitle>
              <CardDescription>Send test notifications</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>Recipient emails (one per line)</Label>
                <textarea
                  className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={testEmailRecipients.join('\n')}
                  onChange={(e) =>
                    setTestEmailRecipients(
                      e.target.value.split('\n').filter((email) => email.trim() !== ''),
                    )
                  }
                  placeholder="email1@example.com&#10;email2@example.com"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Client name (optional)</Label>
                <Input
                  type="text"
                  value={testEmailClientName}
                  onChange={(e) => setTestEmailClientName(e.target.value)}
                  placeholder="Test Client"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Target date (optional, defaults to today)</Label>
                <Input
                  type="date"
                  value={testEmailTargetDate}
                  onChange={(e) => setTestEmailTargetDate(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Notification time (optional, defaults to 09:00)</Label>
                <Input
                  type="time"
                  value={testEmailNotificationTime}
                  onChange={(e) => setTestEmailNotificationTime(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Time range start (optional, defaults to 06:00)</Label>
                <Input
                  type="time"
                  value={testEmailTimeRangeStart}
                  onChange={(e) => setTestEmailTimeRangeStart(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Time range end (optional, defaults to 09:00)</Label>
                <Input
                  type="time"
                  value={testEmailTimeRangeEnd}
                  onChange={(e) => setTestEmailTimeRangeEnd(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Advance minutes (optional, defaults to 15)</Label>
                <Input
                  type="number"
                  value={testEmailAdvanceMinutes}
                  onChange={(e) =>
                    setTestEmailAdvanceMinutes(Number.parseInt(e.target.value, 10) || 15)
                  }
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>Subject override (optional)</Label>
                <Input
                  type="text"
                  value={testEmailSubjectOverride}
                  onChange={(e) => setTestEmailSubjectOverride(e.target.value)}
                  placeholder="Leave empty to use default subject"
                />
              </div>

              <Button
                className="w-full"
                disabled={
                  sendTestEmailMutation.isPending ||
                  testEmailRecipients.length === 0 ||
                  testEmailRecipients.every((email) => email.trim() === '')
                }
                onClick={() => {
                  const validEmails = testEmailRecipients.filter((email) => email.trim() !== '')
                  if (validEmails.length === 0) {
                    return
                  }
                  sendTestEmailMutation.mutate({
                    toEmails: validEmails,
                    subjectOverride:
                      testEmailSubjectOverride.trim() !== ''
                        ? testEmailSubjectOverride
                        : undefined,
                    clientName:
                      testEmailClientName.trim() !== '' ? testEmailClientName : undefined,
                    targetDate: testEmailTargetDate || undefined,
                    notificationTime:
                      testEmailNotificationTime.trim() !== ''
                        ? testEmailNotificationTime
                        : undefined,
                    timeRangeStart: testEmailTimeRangeStart || undefined,
                    timeRangeEnd: testEmailTimeRangeEnd || undefined,
                    advanceMinutes: testEmailAdvanceMinutes || undefined,
                  })
                }}
              >
                Send test email (Step 9)
              </Button>

              <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                {JSON.stringify(
                  {
                    status: sendTestEmailMutation.status,
                    data: sendTestEmailMutation.data ?? null,
                    error: sendTestEmailMutation.error?.message ?? null,
                  },
                  null,
                  2,
                )}
              </div>

              <div className="mt-4 border-t pt-4">
                <h3 className="mb-4 text-lg font-semibold">Test SMS (Placeholder)</h3>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>Recipient phone numbers (one per line)</Label>
                    <textarea
                      className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={testSMSRecipients.join('\n')}
                      onChange={(e) =>
                        setTestSMSRecipients(
                          e.target.value.split('\n').filter((phone) => phone.trim() !== ''),
                        )
                      }
                      placeholder="+1234567890&#10;+0987654321"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Client name (optional)</Label>
                    <Input
                      type="text"
                      value={testSMSClientName}
                      onChange={(e) => setTestSMSClientName(e.target.value)}
                      placeholder="Test Client"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Target date (optional, defaults to today)</Label>
                    <Input
                      type="date"
                      value={testSMSTargetDate}
                      onChange={(e) => setTestSMSTargetDate(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Notification time (optional, defaults to 09:00)</Label>
                    <Input
                      type="time"
                      value={testSMSNotificationTime}
                      onChange={(e) => setTestSMSNotificationTime(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Time range start (optional, defaults to 06:00)</Label>
                    <Input
                      type="time"
                      value={testSMSTimeRangeStart}
                      onChange={(e) => setTestSMSTimeRangeStart(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Time range end (optional, defaults to 09:00)</Label>
                    <Input
                      type="time"
                      value={testSMSTimeRangeEnd}
                      onChange={(e) => setTestSMSTimeRangeEnd(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Advance minutes (optional, defaults to 15)</Label>
                    <Input
                      type="number"
                      value={testSMSAdvanceMinutes}
                      onChange={(e) =>
                        setTestSMSAdvanceMinutes(Number.parseInt(e.target.value, 10) || 15)
                      }
                    />
                  </div>

                  <Button
                    className="w-full"
                    disabled={
                      sendTestSMSMutation.isPending ||
                      testSMSRecipients.length === 0 ||
                      testSMSRecipients.every((phone) => phone.trim() === '')
                    }
                    onClick={() => {
                      const validPhones = testSMSRecipients.filter((phone) => phone.trim() !== '')
                      if (validPhones.length === 0) {
                        return
                      }
                      sendTestSMSMutation.mutate({
                        recipientPhoneNumbers: validPhones,
                        clientName:
                          testSMSClientName.trim() !== '' ? testSMSClientName : undefined,
                        targetDate: testSMSTargetDate || undefined,
                        notificationTime:
                          testSMSNotificationTime.trim() !== ''
                            ? testSMSNotificationTime
                            : undefined,
                        timeRangeStart: testSMSTimeRangeStart || undefined,
                        timeRangeEnd: testSMSTimeRangeEnd || undefined,
                        advanceMinutes: testSMSAdvanceMinutes || undefined,
                      })
                    }}
                  >
                    Send test SMS (placeholder) (Step 10)
                  </Button>

                  <div className="rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(
                      {
                        status: sendTestSMSMutation.status,
                        data: sendTestSMSMutation.data ?? null,
                        error: sendTestSMSMutation.error?.message ?? null,
                      },
                      null,
                      2,
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardShell>
  )
}
